/**
 * Input sanitization utilities for security
 */

// Maximum lengths for different input types
export const INPUT_LIMITS = {
  COMMENT_TEXT: 2000,
  SITE_NAME: 200,
  SITE_DESCRIPTION: 5000,
  USER_DISPLAY_NAME: 100,
  SEARCH_QUERY: 500
};

/**
 * Sanitize text input by removing potentially dangerous content
 * @param {string} text - The text to sanitize
 * @param {number} maxLength - Maximum allowed length
 * @returns {string} - Sanitized text
 */
export const sanitizeText = (text, maxLength = INPUT_LIMITS.COMMENT_TEXT) => {
  if (typeof text !== 'string') {
    return '';
  }

  // Remove null bytes and control characters except newlines and tabs
  let sanitized = text.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');
  
  // Trim whitespace
  sanitized = sanitized.trim();
  
  // Enforce length limit
  if (sanitized.length > maxLength) {
    sanitized = sanitized.substring(0, maxLength);
  }
  
  return sanitized;
};

/**
 * Validate and sanitize comment text
 * @param {string} text - Comment text to validate
 * @returns {Object} - {isValid: boolean, sanitized: string, error?: string}
 */
export const validateCommentText = (text) => {
  if (!text || typeof text !== 'string') {
    return { isValid: false, sanitized: '', error: 'Comment text is required' };
  }

  const sanitized = sanitizeText(text, INPUT_LIMITS.COMMENT_TEXT);
  
  if (sanitized.length === 0) {
    return { isValid: false, sanitized: '', error: 'Comment cannot be empty' };
  }

  if (sanitized.length < 2) {
    return { isValid: false, sanitized, error: 'Comment must be at least 2 characters long' };
  }

  // Check for suspicious patterns
  const suspiciousPatterns = [
    /<script/i,
    /javascript:/i,
    /on\w+\s*=/i,
    /data:text\/html/i,
    /vbscript:/i
  ];

  for (const pattern of suspiciousPatterns) {
    if (pattern.test(sanitized)) {
      return { isValid: false, sanitized, error: 'Comment contains potentially unsafe content' };
    }
  }

  return { isValid: true, sanitized };
};

/**
 * Validate file upload
 * @param {File} file - File to validate
 * @param {Object} options - Validation options
 * @returns {Object} - {isValid: boolean, error?: string}
 */
export const validateFileUpload = (file, options = {}) => {
  const {
    maxSize = 10 * 1024 * 1024, // 10MB default
    allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
    maxNameLength = 255
  } = options;

  if (!file) {
    return { isValid: false, error: 'No file provided' };
  }

  // Check file size
  if (file.size > maxSize) {
    return { isValid: false, error: `File size must be less than ${Math.round(maxSize / 1024 / 1024)}MB` };
  }

  // Check file type
  if (!allowedTypes.includes(file.type)) {
    return { isValid: false, error: 'File type not allowed' };
  }

  // Check filename length and characters
  if (file.name.length > maxNameLength) {
    return { isValid: false, error: 'Filename too long' };
  }

  // Check for suspicious filename patterns
  const suspiciousPatterns = [
    /\.(exe|bat|cmd|scr|pif|com|vbs|js|jar|php|asp|jsp)$/i,
    /[<>:"|?*]/,
    /^\./,
    /\.{2,}/
  ];

  for (const pattern of suspiciousPatterns) {
    if (pattern.test(file.name)) {
      return { isValid: false, error: 'Filename contains invalid characters or extension' };
    }
  }

  return { isValid: true };
};

/**
 * Rate limiting helper - simple in-memory implementation
 * For production, use Redis or similar
 */
class RateLimiter {
  constructor() {
    this.requests = new Map();
  }

  isAllowed(key, maxRequests = 10, windowMs = 60000) {
    const now = Date.now();
    const windowStart = now - windowMs;
    
    if (!this.requests.has(key)) {
      this.requests.set(key, []);
    }
    
    const userRequests = this.requests.get(key);
    
    // Remove old requests outside the window
    const validRequests = userRequests.filter(timestamp => timestamp > windowStart);
    
    if (validRequests.length >= maxRequests) {
      return false;
    }
    
    validRequests.push(now);
    this.requests.set(key, validRequests);
    
    return true;
  }

  cleanup() {
    const now = Date.now();
    const oneHourAgo = now - 3600000; // 1 hour
    
    for (const [key, requests] of this.requests.entries()) {
      const validRequests = requests.filter(timestamp => timestamp > oneHourAgo);
      if (validRequests.length === 0) {
        this.requests.delete(key);
      } else {
        this.requests.set(key, validRequests);
      }
    }
  }
}

export const commentRateLimiter = new RateLimiter();

// Cleanup rate limiter every 10 minutes
setInterval(() => {
  commentRateLimiter.cleanup();
}, 600000);